// Server-supplied URLs (a Stripe checkout or hosted-invoice link) are followed only
// when they are https. Nothing a tenant types reaches them, but a value such as
// `javascript:...` written to `location.href` or an `href` would run in this origin,
// and this origin holds the session.
//
// The check is the WHATWG URL parser, the one a browser uses, so it cannot disagree
// with the browser about the scheme: a leading space, a tab inside the scheme or
// mixed case is normalised the same way in both. Callers write the RETURNED string
// to the sink, never the original, so the value that was checked is the value used.
//
// A URL that carries a username or password is refused too: Stripe links never do,
// and "https://checkout.stripe.com@evil.example/" reads as a Stripe link but goes to
// evil.example.
//
// The host is deliberately not restricted. Stripe serves checkout and invoices from
// its own domains and from customers' custom domains, so an allowlist would refuse
// real links. What this stops is a non-https scheme; an https URL to a host the
// backend chose is the backend's responsibility.

/**
 * @param {unknown} url
 * @returns {string | null} the normalised https URL, or null when `url` is not an
 *   absolute https URL without a username or password (a relative or
 *   protocol-relative URL, another scheme, embedded credentials, a non-string,
 *   unparseable).
 */
export function safeHttpsUrl(url) {
  if (typeof url !== 'string') return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  if (parsed.username !== '' || parsed.password !== '') return null;
  return parsed.href;
}

/**
 * Like {@link safeHttpsUrl}, but throws, for a caller that already has a place to
 * show an error (a `catch` that sets an alert).
 *
 * @param {unknown} url
 * @param {string} [what] how to name the link in the message shown to the user
 * @returns {string} the normalised https URL
 */
export function assertHttps(url, what = 'link') {
  const safe = safeHttpsUrl(url);
  if (safe === null) {
    throw new Error(`The ${what} we received was not a valid https link, so it was not opened. Please try again or contact support.`);
  }
  return safe;
}
